import type { ControllerRenderProps } from 'react-hook-form';
import type { TemplateField } from '../../../overview/models/template.types';
import { fieldControlKind, optionsOf } from '../../models/templateRuntime';
import type { TemplateFormValue, TemplateFormValues } from '../../models/templateRuntime';
import type { TemplateFileFormats } from '../../models/templateFile';
import { TemplateFieldInput } from './TemplateFieldInput';

interface TemplateFieldValueInputProps {
  field: TemplateField;
  /** The control's DOM id; a label's `htmlFor` points at it. */
  id: string;
  value: TemplateFormValue | undefined;
  onChange?: (value: TemplateFormValue) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  /** A File upload's allowed formats (a Purchase Request line's); absent, any format. */
  fileFormats?: TemplateFileFormats;
}

const noop = () => undefined;

/**
 * One template field's control, held by its caller rather than by a form -- the same control
 * `TemplateFieldControl` draws for its type (a Calendar for a Date, a Dropdown for a Dropdown, a number box for a
 * Number), for a value that is not one of a form's: a Purchase Request line's own answers, and the builder's
 * disabled preview of them.
 *
 * `TemplateFieldInput` speaks react-hook-form's field shape; this hands it one built from `value` and `onChange`.
 */
export function TemplateFieldValueInput({
  field,
  id,
  value,
  onChange,
  disabled = false,
  invalid = false,
  describedBy,
  fileFormats,
}: TemplateFieldValueInputProps) {
  const rhf = {
    name: id,
    value,
    onChange: (next: unknown) => {
      onChange?.(next as TemplateFormValue);
    },
    onBlur: noop,
    ref: noop,
  } as unknown as ControllerRenderProps<TemplateFormValues, string>;

  return (
    <TemplateFieldInput
      kind={fieldControlKind(field)}
      id={id}
      labelId={`${id}-label`}
      field={field}
      options={optionsOf(field)}
      rhf={rhf}
      invalid={invalid}
      describedBy={describedBy}
      disabled={disabled}
      marker={null}
      dateStyle="organization"
      fileFormats={fileFormats}
    />
  );
}
